<template>
  <div class="flex flex-col p-3">

    <AppModal v-model="showAddPRN" size="lg" title="Add Used PRNs" hide-footer>
      <div class="border-b border-gray-200 mb-4">
        <nav class="flex space-x-6 px-4" aria-label="Tabs">
          <button
            :class="[option === 'form' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
            @click="option = 'form'"
          >Enter PRNs
          </button>
          <button
            :class="[option === 'upload' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
            @click="option = 'upload'"
          >Upload File
          </button>
        </nav>
      </div>

      <div v-if="option === 'form'" class="space-y-3">
        <textarea
          v-model="prns"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-vertical"
          placeholder="Add PRN(s) here, comma-separated if more than one"
          rows="6"
          @input="updateSize"
        ></textarea>
        <p class="text-xs text-gray-500 italic">Number of PRNs: {{ prnSize }}</p>
        <div class="flex justify-end">
          <button class="btn-primary" @click="save">
            <i class="fa fa-save mr-1"></i> Save
          </button>
        </div>
      </div>

      <div v-if="option === 'upload'" class="space-y-3">
        <input
          type="file"
          @change="handleFileChange"
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          class="block w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary-600 file:cursor-pointer border border-gray-300 rounded bg-white"
        />
        <div class="flex justify-end">
          <button class="btn-primary" @click="saveUpload">
            <i class="fa fa-upload mr-1"></i> Upload
          </button>
        </div>
      </div>
    </AppModal>

    <AppModal v-model="showRemovePRN" size="lg" title="Remove Used PRN" hide-footer>
      <div class="space-y-3">
        <input
          type="text"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          v-model="prnToRemove"
          placeholder="Enter PRN to remove"
        />
        <p class="text-xs text-gray-500 italic">Characters: {{ prnToRemove.length }}</p>
        <div class="flex justify-end">
          <button class="btn-primary bg-red hover:bg-red-600 border-red" @click="removeUsedPrn">
            <i class="fa fa-trash mr-1"></i> Remove
          </button>
        </div>
      </div>
    </AppModal>

    <!-- Header -->
    <div class="flex items-center justify-between py-3">
      <input
        type="text"
        v-model="search"
        class="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm w-64"
        placeholder="Search by PRN"
        @change="searchByPrn"
      />
      <div class="flex gap-2">
        <button class="btn-primary" @click="showAddPRN = true">
          <i class="fa fa-plus mr-1"></i> Add PRN(s)
        </button>
        <button v-if="canReleasePrn" class="btn-primary" @click="showRemovePRN = true">
          <i class="fa fa-times mr-1"></i> Remove PRN
        </button>
      </div>
    </div>

    <!-- Table -->
    <div>
      <table>
        <thead>
        <tr>
          <th>#</th>
          <th>PRN</th>
          <th>Date</th>
        </tr>
        </thead>
        <tbody>
        <template v-if="loading">
          <tr v-for="i in 6" :key="`sk-${i}`" class="animate-pulse">
            <td>
              <div class="h-3 bg-gray-200 rounded w-8"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-48"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-28"></div>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr v-for="(item, idx) in (prnSubmissions.usedPrnResponses ?? [])" :key="idx">
            <td>{{ getPrnNumber(idx).toLocaleString() }}</td>
            <td class="font-bold font-mono">{{ item.prn }}</td>
            <td>{{ dateFormat(item.createdAt) }}</td>
          </tr>
        </template>
        <tr v-if="!loading && (prnSubmissions.usedPrnResponses ?? []).length === 0">
          <td colspan="3" class="text-center py-6 text-gray-400 text-sm">No results found</td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.totalPages > 1" class="flex items-center justify-between mt-4 px-1">
      <button
        class="btn-sm-primary"
        :disabled="currentPage <= 1"
        :class="currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''"
        @click="previous"
      >
        <i class="fa fa-chevron-left mr-1"></i> Previous
      </button>
      <span class="text-xs text-gray-500">Page {{ currentPage }} of {{ pagination.totalPages }}</span>
      <button
        class="btn-sm-primary"
        :disabled="currentPage >= pagination.totalPages"
        :class="currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''"
        @click="next"
      >
        Next <i class="fa fa-chevron-right ml-1"></i>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useAbility } from "@casl/vue";
import AppModal from "@/components/ui/AppModal.vue";
import { dateFormat } from "@/commons/composables/utilities.ts";
import { useBillingStore } from "@/domains/home/services/billing/stores";

const billingStore = useBillingStore();
const { can } = useAbility();

const showAddPRN = ref(false);
const showRemovePRN = ref(false);
const loading = ref(false);
const search = ref("");
const prns = ref("");
const file = ref<File | null>(null);
const option = ref<"form" | "upload">("form");
const prnSize = ref(0);
const prnToRemove = ref("");
const prnSubmissions = ref<any>([]);
const currentPage = ref(1);
const perPage = ref(15);
const pagination = ref({ totalElements: 0, totalPages: 0 });

const canReleasePrn = computed(() => can("can_release_prn", "general"));

const getPrnNumber = (index: number) => index + 1 + (perPage.value * currentPage.value - perPage.value);

onMounted(() => fetch());

const fetch = () => {
  loading.value = true;
  billingStore.fetchUsedPRNs({ page: currentPage.value, perPage: perPage.value });
};

const searchByPrn = () => {
  if (!search.value) return fetch();
  loading.value = true;
  billingStore.filterByPrn({ prn: search.value, page: currentPage.value, perPage: perPage.value });
};

const next = () => {
  if (currentPage.value < pagination.value.totalPages) {
    currentPage.value++;
    fetch();
  }
};

const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetch();
  }
};

const updateSize = () => {
  prnSize.value = prns.value ? prns.value.split(",").length : 0;
};

const save = () => {
  const data = prns.value.split(",");
  prnSize.value = data.length;
  billingStore.addPRNs({ prnList: data });
};

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  file.value = target.files?.[0] ?? null;
};

const saveUpload = () => {
  if (!file.value) return;
  const formData = new FormData();
  formData.set("file", file.value);
  billingStore.uploadPRNs(formData);
};

const removeUsedPrn = () => {
  billingStore.removePRN(prnToRemove.value);
};

watch(() => billingStore.usedPrns, (data) => {
  loading.value = false;
  if (data) {
    prnSubmissions.value = data;
    pagination.value = { totalPages: data.totalPages ?? 0, totalElements: data.totalElements ?? 0 };
  }
});

watch(() => billingStore.prnAdded, (data) => {
  if (data) {
    showAddPRN.value = false;
    prns.value = "";
    prnSize.value = 0;
    fetch();
  }
});

watch(() => billingStore.prnRemoved, (data) => {
  if (data) {
    showRemovePRN.value = false;
    prnToRemove.value = "";
    fetch();
  }
})

watch(() => billingStore.prnError, (data) => {
  if (data) loading.value = false;
});
</script>
